import { useState } from "react"
import { useNavigate, Link } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import ModuleLayout from "@/components/admin/ModuleLayout"
import { RowActionsMenu } from "@/components/admin/RowActionsMenu"
import { ConfirmDialog } from "@/components/admin/ConfirmDialog"
import Badge from "@/components/ui/badge/Badge"
import DataTable, { type DataTableColumn } from "@/components/ui/table/DataTable"
import { staffRolesAdmin, type StaffRole } from "@/lib/api/staffRolesClient"
import { toastError, toastSuccess } from "@/lib/toast"
import { getErrorMessage } from "@/lib/errors"
import { PencilIcon, TrashBinIcon } from "@/icons"
import { STAFF_PROFILE_LABELS } from "@alascorporation/access-control"

const ROLES_QUERY_KEY = ["staff-roles"]
// Only Administrador is protected from edit — Docente/Gestor CCCD are also
// isBuiltIn (drives the "Predefinido" badge below) but are editable.
const ADMIN_ROLE_NAME = STAFF_PROFILE_LABELS.admin

export default function RolesListPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [roleToDelete, setRoleToDelete] = useState<StaffRole | null>(null)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<"" | "builtin" | "custom">("")

  const { data: allRoles = [], isLoading, error } = useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: () => staffRolesAdmin.list(),
  })
  const roles = allRoles
    .filter((r) => !typeFilter || (typeFilter === "builtin" ? r.isBuiltIn : !r.isBuiltIn))
    .filter((r) => !search.trim() || r.name.toLowerCase().includes(search.trim().toLowerCase()))

  const deleteMutation = useMutation({
    mutationFn: (id: string) => staffRolesAdmin.remove(id),
    onSuccess: () => {
      toastSuccess("Rol eliminado")
      qc.invalidateQueries({ queryKey: ROLES_QUERY_KEY })
      setRoleToDelete(null)
    },
    onError: (err) => toastError(err),
  })

  const columns: DataTableColumn<StaffRole>[] = [
    {
      key: "name",
      label: "Nombre",
      render: (role) =>
        role.name === ADMIN_ROLE_NAME ? (
          <span className="font-medium text-gray-800 dark:text-white/90">{role.name}</span>
        ) : (
          <Link
            to={`/settings/roles/${role.id}/edit`}
            className="font-medium text-gray-800 hover:underline dark:text-white/90"
          >
            {role.name}
          </Link>
        ),
    },
    {
      key: "type",
      label: "Tipo",
      render: (role) => (
        <Badge color={role.isBuiltIn ? "light" : "primary"} size="sm">
          {role.isBuiltIn ? "Predefinido" : "Personalizado"}
        </Badge>
      ),
    },
    {
      key: "permissionKeys",
      label: "Permisos",
      render: (role) => {
        const resourceCount = new Set(role.permissionKeys.map((key) => key.slice(0, key.lastIndexOf(":")))).size
        return (
          <span className="text-gray-500">
            {resourceCount} {resourceCount === 1 ? "sección" : "secciones"} · {role.permissionKeys.length}{" "}
            {role.permissionKeys.length === 1 ? "permiso" : "permisos"}
          </span>
        )
      },
    },
    {
      key: "actions",
      label: "",
      headerAriaLabel: "Acciones",
      align: "right",
      render: (role) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: "Editar",
                icon: <PencilIcon />,
                onClick: () => navigate(`/settings/roles/${role.id}/edit`),
                disabled: role.name === ADMIN_ROLE_NAME,
                requires: "staff:manage-roles:edit",
              },
              {
                label: "Eliminar",
                icon: <TrashBinIcon />,
                onClick: () => setRoleToDelete(role),
                variant: "danger",
                disabled: role.isBuiltIn,
                requires: "staff:manage-roles:edit",
              },
            ]}
          />
        </div>
      ),
    },
  ]

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Sistema", "Roles y permisos"]}
      title="Roles y permisos"
      subtitle="Perfiles predefinidos y personalizados del panel de gestión"
      tabs={[{ label: "Roles", to: "/settings/roles", active: true }]}
      metrics={[
        { label: "Total", value: String(allRoles.length) },
        { label: "Predefinidos", value: String(allRoles.filter((r) => r.isBuiltIn).length) },
        { label: "Personalizados", value: String(allRoles.filter((r) => !r.isBuiltIn).length) },
      ]}
      toolbar={{
        search: { value: search, onChange: setSearch, placeholder: "Buscar rol..." },
        filters: [
          {
            type: "select",
            key: "type",
            label: "Tipo",
            value: typeFilter,
            onChange: (v) => setTypeFilter(v as "" | "builtin" | "custom"),
            options: [
              { label: "Todos", value: "" },
              { label: "Predefinido", value: "builtin" },
              { label: "Personalizado", value: "custom" },
            ],
          },
        ],
        onRefresh: () => qc.invalidateQueries({ queryKey: ROLES_QUERY_KEY }),
        refreshing: isLoading,
        onCreate: () => navigate("/settings/roles/new"),
        createLabel: "+ Nuevo rol",
        requires: "staff:manage-roles:edit",
      }}
    >
      {error ? (
        <p role="alert" aria-live="assertive" className="mb-4 text-sm text-error-500">
          {getErrorMessage(error)}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        rows={roles}
        rowKey={(role) => role.id}
        loading={isLoading}
        emptyMessage="Sin roles todavía."
        page={1}
        pageSize={Math.max(roles.length, 1)}
        total={roles.length}
        onPageChange={() => {}}
      />

      <ConfirmDialog
        isOpen={roleToDelete !== null}
        title={`¿Eliminar el rol "${roleToDelete?.name ?? ""}"?`}
        message="Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirming={deleteMutation.isPending}
        onConfirm={() => roleToDelete && deleteMutation.mutate(roleToDelete.id)}
        onCancel={() => setRoleToDelete(null)}
      />
    </ModuleLayout>
  )
}
